import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable, dependency-free math CAPTCHA.
 *
 * Fetches a challenge from `/api/captcha/new`, shows the question + a small
 * numeric input, and calls `onChange({ token, answer })` on every keystroke.
 *
 * Parent components forward that object into their POST body — the server
 * validates via the same JWT that generated the question.
 *
 * Expose a `refresh()` function via `refreshKey` prop bump if the parent
 * needs to force a new challenge after a failed submit (single-use tokens).
 */
export default function Captcha({ onChange, refreshKey = 0, className = "", testId = "captcha" }) {
  const [question, setQuestion] = useState("");
  const [token, setToken] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchNew = useCallback(() => {
    setLoading(true);
    setAnswer("");
    axios
      .get(`${API}/captcha/new`)
      .then((r) => {
        setToken(r.data.token || "");
        setQuestion(r.data.question || "");
      })
      .catch(() => {
        setToken("");
        setQuestion("Could not load CAPTCHA");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchNew(); }, [fetchNew, refreshKey]);
  useEffect(() => { onChange && onChange({ token, answer }); }, [token, answer, onChange]);

  return (
    <div className={`space-y-1.5 ${className}`} data-testid={testId}>
      <label className="block text-xs font-medium tracking-[0.14em] uppercase text-[#4B5563]">
        Verify you're human
      </label>
      <div className="flex items-center gap-2">
        <div
          className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-[#F7F8F6] text-sm text-[#1F2937] font-mono select-none"
          data-testid={`${testId}-question`}
          aria-label="CAPTCHA question"
        >
          {loading ? "Loading…" : question || "—"}
        </div>
        <input
          type="text"
          inputMode="numeric"
          pattern="-?[0-9]*"
          value={answer}
          onChange={(e) => setAnswer(e.target.value.replace(/[^0-9-]/g, ""))}
          placeholder="Your answer"
          maxLength={5}
          className="w-28 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30"
          data-testid={`${testId}-answer`}
          aria-label="CAPTCHA answer"
        />
        <button
          type="button"
          onClick={fetchNew}
          disabled={loading}
          className="p-2 rounded-lg border border-[#E2E8F0] bg-white text-[#4B5563] hover:text-[#C2410C] hover:border-[#C2410C]/40 transition disabled:opacity-40"
          title="Refresh challenge"
          aria-label="Refresh CAPTCHA"
          data-testid={`${testId}-refresh`}
        >
          <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
    </div>
  );
}
